import * as z from "zod";
import {
  anyOfFilters,
  code,
  codeName,
  countByFiltersInput,
  cursorPagination,
  decimal,
  flag,
  pagination,
  version,
} from "../index";
import { syncStatuses } from "../til";
import { poStatuses } from "./enums";

// Purchase order, header and lifecycle (50-api/procurement.md §2.1; FR-M5-02…07).

/** Why a PO is reworked or cancelled, a proposal made or refused: it goes to the audit and to who reads the answer. */
export const poReason = z.string().trim().min(1).max(2000);

/** `purchase_order_line.closure_reason` holds 200 characters. */
export const MAX_CLOSURE_REASON = 200;

/** Why a line is cancelled or closed. */
export const closureReason = z.string().trim().min(1).max(MAX_CLOSURE_REASON);

/** PO by id (uuid) or by number. */
export const getPurchaseOrderInput = z.object({ key: z.string().min(1).max(36) });
export type GetPurchaseOrderInput = z.input<typeof getPurchaseOrderInput>;

/** Orders of the PO list; the id breaks ties. */
const purchaseOrderSorts = ["-updatedAt", "poNumber", "deliveryWindowOpen"] as const;

const purchaseOrderFilters = {
  status: z.enum(poStatuses).optional(),
  brand: z.uuid().optional(),
  supplier: z.uuid().optional(),
  /** Code of the season. */
  season: code.optional(),
  /** Code of the destination warehouse. */
  destination: code.optional(),
  /** How the PO stands towards Business Central: `sync_failed` is the queue of who relaunches (I-02 §6). */
  bcSyncStatus: z.enum(syncStatuses).optional(),
  /** Only the ones with proposals of the supplier that wait for Operations (FR-M5-05). */
  pendingChanges: flag.optional(),
  /**
   * Only the ones the Actor answers for as their supplier (FR-M5-05, ADR-168): all of them for Tomorrow, which answers
   * for every supplier; for an external user, those of the suppliers it works for, not those its brand only reads.
   */
  asSupplier: flag.optional(),
  /**
   * Confirmed, with an open line whose units no inbound submitted announces yet (FR-M11-01, ADR-170): the supplier's
   * documents to upload. The same count as "New inbound" (`qtyPo` − `qtyAnnounced` of the candidates).
   */
  toAnnounce: flag.optional(),
  /**
   * Not confirmed by its supplier a cadence after the last reminder of its series (B-37, ADR-245): published, or in
   * validation with every proposal answered. Operations' queue "POs not confirmed".
   */
  remindersOver: flag.optional(),
  /** Part of the number, by its letters and digits (`po0001` finds `PO-0001`, ADR-117). */
  q: z.string().trim().min(1).max(30).optional(),
};

/** The POs in at least one of these sets of the filters (ADR-302): the queues of the POs, to do. */
const anyOfPurchaseOrders = { anyOf: anyOfFilters(purchaseOrderFilters).optional() };

/** FR-M5-04. An external Actor sees a PO from its publication on, whatever the filter. */
export const listPurchaseOrdersInput = z.object({
  ...purchaseOrderFilters,
  ...anyOfPurchaseOrders,
  sort: z.enum(purchaseOrderSorts).default("-updatedAt"),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListPurchaseOrdersInput = z.input<typeof listPurchaseOrdersInput>;

/** How many POs the list has with the same filters, and when the one untouched for longest was updated (ADR-086). */
export const countPurchaseOrdersInput = z.object({ ...purchaseOrderFilters, ...anyOfPurchaseOrders });
export type CountPurchaseOrdersInput = z.input<typeof countPurchaseOrdersInput>;
/** The same count under several filters, in one call and one transaction (ADR-269): the queues of the home. */
export const countPurchaseOrdersByFiltersInput = countByFiltersInput(countPurchaseOrdersInput);
export type CountPurchaseOrdersByFiltersInput = z.input<typeof countPurchaseOrdersByFiltersInput>;

/**
 * An action on a PO (approve, publish…). `version` is the one the client read: a PO changed in the meantime, in its
 * header or in its lines, is left as it is (409).
 */
export const purchaseOrderActionInput = z.object({ purchaseOrderId: z.uuid(), version });
export type PurchaseOrderActionInput = z.input<typeof purchaseOrderActionInput>;

/** The gateway routes split the input: the id in the path, the rest in the body. */
export const purchaseOrderParams = purchaseOrderActionInput.pick({ purchaseOrderId: true });
export const purchaseOrderVersionBody = purchaseOrderActionInput.pick({ version: true });

/** PATCH, on a `generated` PO: an absent field stays as it is, `null` empties it. */
export const updatePurchaseOrderInput = purchaseOrderActionInput.extend({
  paymentTerms: code.nullable().optional(),
  incoterm: z.string().trim().length(3).nullable().optional(),
  shipTo: z.string().trim().min(1).max(2000).nullable().optional(),
  shippingMethod: z.string().trim().min(1).max(100).nullable().optional(),
  deliveryWindowOpen: z.iso.date().nullable().optional(),
  deliveryWindowClose: z.iso.date().nullable().optional(),
  memo: z.string().trim().min(1).max(2000).nullable().optional(),
});
export type UpdatePurchaseOrderInput = z.input<typeof updatePurchaseOrderInput>;
export const updatePurchaseOrderBody = updatePurchaseOrderInput.omit({ purchaseOrderId: true });

/** The reason goes to the audit, to the revision of a rework and to `cancelReason` of a cancellation. */
export const purchaseOrderReasonInput = purchaseOrderActionInput.extend({ reason: poReason });
export type PurchaseOrderReasonInput = z.input<typeof purchaseOrderReasonInput>;
export const purchaseOrderReasonBody = purchaseOrderReasonInput.omit({ purchaseOrderId: true });

/** Why a confirmed PO is closed: it becomes the closure reason of every line still open, hence its length. */
export const closePurchaseOrderInput = purchaseOrderActionInput.extend({ reason: closureReason });
export type ClosePurchaseOrderInput = z.input<typeof closePurchaseOrderInput>;
export const closePurchaseOrderBody = closePurchaseOrderInput.omit({ purchaseOrderId: true });

/** The values of the PO filters and form (ADR-048): nothing to choose. */
export const getPurchaseOrderOptionsInput = z.object({});
export type GetPurchaseOrderOptionsInput = z.input<typeof getPurchaseOrderOptionsInput>;

/** Newest first. */
export const getPurchaseOrderHistoryInput = z.object({
  purchaseOrderId: z.uuid(),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 100 }),
});
export type GetPurchaseOrderHistoryInput = z.input<typeof getPurchaseOrderHistoryInput>;
export const purchaseOrderHistoryQuery = getPurchaseOrderHistoryInput.omit({ purchaseOrderId: true });

/**
 * A PO: in the list, alone and after an action. Totals come from the lines not cancelled, on the confirmed quantity
 * where there is one, otherwise on the ordered one.
 */
export const purchaseOrderDto = z
  .object({
    id: z.uuid(),
    poNumber: z.string(),
    status: z.enum(poStatuses),
    brandOrgId: z.uuid(),
    brand: z.string(),
    supplierOrgId: z.uuid(),
    supplier: z.string(),
    /**
     * The Actor answers for this PO as its supplier (`actsFor`, the rule of `assertSupplierSide`, ADR-169): always for
     * Tomorrow; for an external user, a PO of a supplier it works for. Only then it confirms and proposes.
     */
    asSupplier: z.boolean(),
    /** Code of the company of the group that buys. */
    legalEntity: z.string(),
    season: z.string().nullable(),
    /** The destination warehouse: a PO has one (FR-M5-01). */
    destination: codeName,
    currency: z.string(),
    exchangeRate: decimal.nullable().optional(),
    paymentTerms: z.string().nullable(),
    incoterm: z.string().nullable(),
    shipTo: z.string().nullable(),
    shippingMethod: z.string().nullable(),
    deliveryWindowOpen: z.iso.date().nullable(),
    deliveryWindowClose: z.iso.date().nullable(),
    memo: z.string().nullable(),
    /** A line was cancelled, or its quantity is no longer the original one. */
    isAmended: z.boolean(),
    lineCount: z.number().int(),
    openLineCount: z.number().int(),
    qtyOrdered: z.number().int(),
    qtyReceived: z.number().int(),
    /** In the currency of the PO. */
    totalCost: decimal.optional(),
    /** How many times the PO went back to `generated` (FR-M5-06). Only Tomorrow reads it (ADR-170). */
    revisionCount: z.number().int().optional(),
    /** Proposals of the supplier that wait for Operations (FR-M5-05): none, and the PO can be confirmed. */
    pendingChangeCount: z.number().int(),
    /** Only Tomorrow reads it (ADR-170): the supplier sees the PO from its publication. */
    approvedAt: z.iso.datetime().nullable().optional(),
    publishedAt: z.iso.datetime().nullable(),
    confirmedAt: z.iso.datetime().nullable(),
    closedAt: z.iso.datetime().nullable(),
    cancelReason: z.string().nullable(),
    /**
     * How the last state sent to Business Central stands (I-02): `not_synced` until the PO is confirmed. Only Tomorrow
     * reads it, and the next one (ADR-170).
     */
    bcSyncStatus: z.enum(syncStatuses).optional(),
    /** The states of the PO sent to Business Central so far: `revision` of the last I-02. */
    bcSyncRevision: z.number().int().optional(),
    version: z.number().int(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "PurchaseOrder" });

/**
 * The values the PO filters and form offer (ADR-048): brands and suppliers the Actor may work for, seasons and
 * warehouses not archived.
 */
export const purchaseOrderOptionsDto = z
  .object({
    brands: z.array(z.object({ orgId: z.uuid(), name: z.string() })),
    suppliers: z.array(z.object({ orgId: z.uuid(), name: z.string() })),
    seasons: z.array(codeName),
    destinations: z.array(codeName),
    paymentTerms: z.array(codeName),
    incoterms: z.array(codeName),
  })
  .meta({ id: "PurchaseOrderOptions" });
